import {useCallback, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {getAnalyticsPageName, getAnonymousId} from 'modules/meta/selectors'
import {getOfferEntity} from 'modules/sapiSearch/selectors'
import {getTopOffers} from 'modules/sapiSearch/utils'
import {getProfileUrlParam} from 'modules/search/selectors'
import {v4 as uuid} from 'uuid'

import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {
  trackEvent,
  trackProviderRedirect
} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page
} from '@vio/core/src/analytics/types/Events'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {augmentBookUriWithAnonymousId} from '@vio/core/src/offer/business/offers'
import {isMetaProfile} from '@vio/core/src/profile'

import {
  setBackgroundTabTriggered,
  shouldTriggerExpediaBackgroundTabForHotelCard
} from './helpers'

interface Params {
  hotelId: string
  hasOtaRedirect: boolean
  originalOnShowHotelDetails: () => void
}

/**
 * Hook to enhance mobile hotel card clicks with Boost Expedia background tab redirect experiment
 *
 * Added as part of 2a7f5ff0-expedia-back-tab
 * To remove when cleaning up: Delete this hook call and import from mobile hotel card components
 */
export const useBoostExpediaBackgroundTabHotelCard = ({
  hotelId,
  hasOtaRedirect,
  originalOnShowHotelDetails
}: Params) => {
  const toggle = useToggle()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const anonymousId = useSelector(getAnonymousId)
  const profile = useSelector(getProfileUrlParam)
  const pageName = useSelector(getAnalyticsPageName)
  const {isMobile} = useDeviceLayout()
  const {search} = useBrandCapabilities()

  const offerEntity = useSelector(state => getOfferEntity(state, hotelId))
  const topOffers = offerEntity ? getTopOffers(offerEntity) : []

  const {shouldTrigger, expOffer} =
    shouldTriggerExpediaBackgroundTabForHotelCard({
      topOffers,
      isEligible:
        isMobile &&
        search.hasExpediaBackTab &&
        isMetaProfile(profile) &&
        pageName === Page.Search &&
        !hasOtaRedirect
    })

  // Generate direct EXP offer URL (bypassing provider redirect screen)
  // We add anonymousId for booking attribution tracking
  const expOfferDirectUrl = useMemo(() => {
    if (!expOffer?.url) return ''
    return augmentBookUriWithAnonymousId(anonymousId, expOffer.url)
  }, [expOffer, anonymousId])

  const enhancedOnShowHotelDetails = useCallback(() => {
    if (shouldTrigger && expOffer) {
      const isExperimentEnabled = toggle(
        '2a7f5ff0-expedia-back-tab',
        false,
        true
      )

      // Set on both variants so A only triggers where B would open the back-tab
      setBackgroundTabTriggered()

      if (isExperimentEnabled) {
        // Fire standard ProviderRedirected event for analytics
        const redirectId = uuid()
        dispatchTeamEvent(
          trackProviderRedirect({
            offerId: expOffer.id,
            intermediaryProvider: expOffer.intermediaryProvider,
            redirectId
          })
        )

        // After a short timeout (to allow tracking events), redirect current tab to EXP offer
        // Direct redirect bypassing provider redirect screen
        // This timeout needs to be a bit more than usual because originalOnShowHotelDetails contains window.open
        // which can conflict with window.location.href change
        setTimeout(() => {
          window.location.href = expOfferDirectUrl
        }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY * 2)
      }

      dispatchTeamEvent(
        trackEvent({
          category: Category.System,
          entity: Entity.ExpediaBackTab,
          action: Action.Triggered,
          page: Page.Search,
          payload: {
            triggerType: 'hotelCard'
          }
        })
      )
    }

    originalOnShowHotelDetails()
  }, [
    shouldTrigger,
    expOffer,
    originalOnShowHotelDetails,
    toggle,
    dispatchTeamEvent,
    expOfferDirectUrl
  ])

  return {forceClickHandler: shouldTrigger, enhancedOnShowHotelDetails}
}
